/**
 * Axios configuration module for API requests.
 *
 * This module exports a pre-configured axios instance (`apiClient`) for making HTTP requests
 * throughout the application. Global interceptors and default settings can be added here.
 *
 * @module axios.config
 */
import { BASE_API_URL } from '@app/app-constants'
import loginRedirect from '@app/utils/loginRedirect'
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

const apiClient = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true,
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers = config.headers ?? {}

  try {
    const raw = localStorage.getItem('client_name')
    const tempRoles = localStorage.getItem('temp_user_role')
    const value = raw && raw.startsWith('"') ? JSON.parse(raw) : raw
    if (value) {
      config.headers['ui-client-name'] = value
    } else {
      delete config.headers['ui-client-name']
    }

    if (tempRoles) {
      config.headers['ui-temp-roles'] = tempRoles
    } else {
      delete config.headers['ui-temp-roles']
    }
  } catch {
    delete config.headers['ui-temp-roles']
  }

  return config
})

apiClient.interceptors.response.use(
  (res: AxiosResponse) => {
    return res
  },
  async (err: AxiosError) => {
    if (!err.response || !err.config) return Promise.reject(err)
    const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const url = (originalRequest.url ?? '').toString()
    const isRefreshCall = url.includes('/user/refresh_token')
    if (isRefreshCall) return Promise.reject(err)

    interface ErrorResponse {
      error?: string
    }
    const data: string | ErrorResponse | unknown = err.response.data
    const message = typeof data === 'string' ? data : (data as ErrorResponse)?.error
    const isExpired401 =
      err.response.status === 401 &&
      (message === 'TokenExpired' ||
        message === 'NotAuthorizedException' ||
        (typeof message === 'string' && message.toLowerCase().includes('unauthorized')))

    const path = window.location.pathname
    const search = window.location.search

    if (isExpired401 && path.startsWith('/app') && search !== '?login=success') {
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('client_name')
      loginRedirect(path)
    }
    return Promise.reject(err)
  },
)

export default apiClient
